import React, { FC, Fragment } from 'react';
import { Box, Grid, Link, Typography, TypographyVariant } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';

import { GridLayoutSizes } from 'types/app';
import { useContent } from 'hooks';
import { generateLink } from 'Links';
import { getFullName } from 'helpers';

import { getProviderDisplay, getTitle, hasCreator, isEngageable, isTextOnly } from '../helpers';
import { useCanShare } from '../hooks';
import { Engage, Share } from '../widgets';

interface Props {
  variant: GridLayoutSizes;
}

const useStyles = makeStyles(theme => ({
  spacer: {
    marginLeft: theme.spacing(0.5),
    marginRight: theme.spacing(0.5),
  },
}));

const ContentSection: FC<Props> = ({ variant }) => {
  const classes = useStyles();
  const theme = useTheme();
  const content = useContent();

  const showShare = useCanShare(content);
  const showEngage = isEngageable(content);
  const title = getTitle(content);

  const providerDisplay = getProviderDisplay(content);
  const hasPostCreator = hasCreator(content);
  const postCreator = getFullName(content.user);
  const LinkContentComponent = generateLink(`/content/${content.content_id}`);
  const LinkUserComponent = hasPostCreator && generateLink(`/profile/${content?.user?.id}`);

  let titleVariant: TypographyVariant;
  let titleStyle;

  switch (variant) {
    case GridLayoutSizes.Small:
      titleVariant = 'subtitle2';
      titleStyle = theme.mixins.clampInline(2);
      break;
    case GridLayoutSizes.Medium:
      titleVariant = 'h6';
      titleStyle = theme.mixins.clampInline(2);
      break;
    case GridLayoutSizes.Large:
      titleVariant = 'h4';
      titleStyle = theme.mixins.clampInline(4);
      break;
  }

  if (isTextOnly(content)) {
    titleStyle = theme.mixins.clampInline(8);
    titleVariant = 'body1';
  }

  function renderMeta() {
    if (!providerDisplay && !hasPostCreator) {
      return null;
    }

    return (
      <Grid container alignItems="center" wrap="nowrap">
        {providerDisplay && (
          <Grid item zeroMinWidth>
            <Typography noWrap color="textSecondary">
              {providerDisplay}
            </Typography>
          </Grid>
        )}
        {hasPostCreator && (
          <Fragment>
            <Grid item>
              <span className={classes.spacer}>&mdash;</span>
            </Grid>
            <Grid item zeroMinWidth>
              <Link
                display="block"
                underline="none"
                component={LinkUserComponent}
                noWrap
                variant="body2"
                color="textSecondary"
              >
                {postCreator}
              </Link>
            </Grid>
          </Fragment>
        )}
      </Grid>
    );
  }

  return (
    <Fragment>
      {title && (
        <Link
          style={titleStyle}
          display="block"
          component={LinkContentComponent}
          underline="none"
          variant={titleVariant}
        >
          {title}
        </Link>
      )}
      {renderMeta()}
      {(showEngage || showShare) && (
        <Box marginTop={2}>
          <Grid container spacing={1}>
            {showShare && (
              <Grid item>
                <Share />
              </Grid>
            )}
            {showEngage && (
              <Grid item>
                <Engage />
              </Grid>
            )}
          </Grid>
        </Box>
      )}
    </Fragment>
  );
};

export default ContentSection;
